/**
 * SENTINEL C2 Edge Intelligence Platform — API Service Layer
 *
 * Handles all communication with the backend API.
 * Uses VITE_API_URL environment variable, defaults to http://localhost:8001/api
 */

import type {
  ForceCompositionResponse,
  AssetDetail,
  TempoAlert,
  FusionCluster,
  AARCase,
  AARScorecard,
  PredictionResponse,
  IntelJob,
  EdgeStatus,
  ModelInfo,
  AuditEntry,
  TheaterFuelSummary,
  FuelState,
  TheaterOrdnanceSummary,
  OrdnanceStockpile,
  AssetLoadout,
  SustainabilityResult,
} from '../types'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

// ===== Generic API Response =====
interface ApiResponse<T> {
  data?: T
  error?: string
}

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    const data = await response.json()

    if (!response.ok) {
      return { error: data.error || data.detail || `HTTP ${response.status}` }
    }

    return { data }
  } catch (error) {
    console.error('API Error:', error)
    return { error: error instanceof Error ? error.message : 'Network error' }
  }
}

// ===== Health =====
export async function checkHealth(): Promise<ApiResponse<{
  status: string
  service: string
  version: string
  uptime_seconds: number
  models_loaded: boolean
  edge_mode: boolean
}>> {
  return fetchApi('/health')
}

// ===== Edge Status =====
export async function getEdgeStatus(): Promise<ApiResponse<EdgeStatus>> {
  return fetchApi('/edge/status')
}

export async function toggleDDIL(level: number): Promise<ApiResponse<{
  ddil_level: number
  connectivity: string
}>> {
  return fetchApi('/edge/toggle-ddil', {
    method: 'POST',
    body: JSON.stringify({ level }),
  })
}

export async function triggerSync(): Promise<ApiResponse<{
  synced_predictions: number
  sync_time: string
  cloud_agreement_pct: number
}>> {
  return fetchApi('/edge/sync', { method: 'POST' })
}

// ===== Force Composition =====
export async function getForceComposition(domain?: string): Promise<ApiResponse<ForceCompositionResponse>> {
  const params = domain ? `?domain=${domain}` : ''
  return fetchApi(`/force-composition${params}`)
}

export async function getAssetDetail(assetId: string): Promise<ApiResponse<AssetDetail>> {
  return fetchApi(`/force-composition/${encodeURIComponent(assetId)}`)
}

// ===== Tempo Alerts =====
export async function getTempoAlerts(limit?: number): Promise<ApiResponse<TempoAlert[]>> {
  const params = limit ? `?limit=${limit}` : ''
  return fetchApi(`/tempo/alerts${params}`)
}

export async function getTempoHistory(assetId: string): Promise<ApiResponse<{
  asset_id: string
  periods: number
  history: Array<{ period: number; tempo: string; confidence: number }>
}>> {
  return fetchApi(`/tempo/${encodeURIComponent(assetId)}/history`)
}

// ===== Fusion Clusters =====
export async function getFusionClusters(): Promise<ApiResponse<FusionCluster[]>> {
  return fetchApi('/fusion/clusters')
}

// ===== Intel Assessment =====
export async function generateIntel(assetId: string, reportType: string = 'SITREP'): Promise<ApiResponse<IntelJob>> {
  return fetchApi(`/intel/generate/${encodeURIComponent(assetId)}?report_type=${reportType}`, {
    method: 'POST',
  })
}

export async function getIntelStatus(jobId: string): Promise<ApiResponse<IntelJob>> {
  return fetchApi(`/intel/status/${jobId}`)
}

// ===== AAR Engine =====
export async function getAARCases(): Promise<ApiResponse<AARCase[]>> {
  return fetchApi('/aar/cases')
}

export async function getAARScorecard(): Promise<ApiResponse<AARScorecard>> {
  return fetchApi('/aar/scorecard')
}

// ===== Audit Trail =====
export async function getAuditEntries(limit: number = 50): Promise<ApiResponse<AuditEntry[]>> {
  return fetchApi(`/audit/entries?limit=${limit}`)
}

// ===== Prediction =====
export async function predict(assetType: string, features: number[]): Promise<ApiResponse<PredictionResponse>> {
  return fetchApi('/predict', {
    method: 'POST',
    body: JSON.stringify({ asset_type: assetType, features }),
  })
}

// ===== Model Info =====
export async function getModelInfo(): Promise<ApiResponse<{
  models: ModelInfo[]
  total_edge_size_mb: number
  ensemble_weights: Record<string, Record<string, number>>
}>> {
  return fetchApi('/model/info')
}

// ===== Logistics — Fuel & Ordnance =====
export async function getTheaterFuel(): Promise<ApiResponse<TheaterFuelSummary>> {
  return fetchApi('/logistics/fuel/theater')
}

export async function getAssetFuel(assetId: string): Promise<ApiResponse<FuelState>> {
  return fetchApi(`/logistics/fuel/${encodeURIComponent(assetId)}`)
}

export async function getTheaterOrdnance(): Promise<ApiResponse<TheaterOrdnanceSummary>> {
  return fetchApi('/logistics/ordnance/theater')
}

export async function getAssetLoadout(assetId: string): Promise<ApiResponse<AssetLoadout>> {
  return fetchApi(`/logistics/ordnance/${encodeURIComponent(assetId)}`)
}

export async function getOrdnanceCompatibility(): Promise<ApiResponse<{
  compatibility: Record<string, Record<string, number>>
  catalog: Record<string, { name: string; short_name: string; category: string; weight_lbs: number; unit_cost_k: number }>
}>> {
  return fetchApi('/logistics/ordnance/compatibility')
}

export async function calcSustainability(sortiesPerDay: number, tempoLabel: string = 'CUSTOM'): Promise<ApiResponse<SustainabilityResult>> {
  return fetchApi('/logistics/sustainability', {
    method: 'POST',
    body: JSON.stringify({ sorties_per_day: sortiesPerDay, tempo_label: tempoLabel }),
  })
}

// ===== Theater Configuration =====
export async function updateOrdnanceOnHand(munitionId: string, onHand: number): Promise<ApiResponse<OrdnanceStockpile>> {
  return fetchApi(`/logistics/ordnance/${encodeURIComponent(munitionId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ on_hand: onHand }),
  })
}

export async function applyTheaterPreset(preset: string): Promise<ApiResponse<{
  preset: string
  description: string
  changes: { ordnance_changes: Array<{ munition_id: string; old_on_hand: number; new_on_hand: number }>; asset_changes: unknown[] }
}>> {
  return fetchApi(`/theater/preset/${encodeURIComponent(preset)}`, { method: 'POST' })
}

export async function importTheaterConfig(config: {
  ordnance_overrides: Record<string, number>
  asset_overrides: Record<string, Record<string, unknown>>
}): Promise<ApiResponse<{ status: string; changes: unknown }>> {
  return fetchApi('/theater/import', {
    method: 'POST',
    body: JSON.stringify(config),
  })
}

export async function exportTheaterConfig(): Promise<ApiResponse<{
  ordnance: Record<string, { munition_name: string; on_hand: number; days_of_supply: number; status: string }>
  assets: Record<string, { asset_type: string; assigned_count: number; mc_rate: number }>
}>> {
  return fetchApi('/theater/export')
}

export async function resetTheater(): Promise<ApiResponse<{ status: string; message: string }>> {
  return fetchApi('/theater/reset', { method: 'POST' })
}

export async function getTheaterPresets(): Promise<ApiResponse<Record<string, { name: string; description: string }>>> {
  return fetchApi('/theater/presets')
}

// ===== GAO Data =====
export async function getMCRates(): Promise<ApiResponse<Record<string, unknown>>> {
  return fetchApi('/data/mc-rates')
}

// ===== Utility Functions =====
export function formatPercent(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return 'N/A'
  return `${(value * 100).toFixed(1)}%`
}

export function formatScore(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return 'N/A'
  return `${value.toFixed(0)}/100`
}

export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return 'N/A'
  return new Intl.NumberFormat().format(value)
}

export function timeAgo(timestamp: string): string {
  const now = new Date()
  const then = new Date(timestamp)
  const diffMs = now.getTime() - then.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  return `${diffDay}d ago`
}
